import React from 'react';
import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'August 23, 2026';
const CONTACT_EMAIL = 'privacy@momorox.com';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-bold text-white mb-4">{title}</h2>
    <div className="space-y-3 text-gray-400 text-sm leading-relaxed">{children}</div>
  </div>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-dark-base text-gray-300">
      {/* Header */}
      <div className="border-b border-white/10 bg-dark-lighter">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-black text-gradient">MomoroX</Link>
          <span className="text-gray-500 text-sm">Privacy Policy</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-black text-white mb-2">Privacy Policy</h1>
        <p className="text-gray-500 text-sm mb-12">Effective date: {EFFECTIVE_DATE}</p>

        <Section title="1. Overview">
          <p>
            MomoroX, Inc. ("MomoroX", "we", "us", or "our") operates the MomoroX Concierge platform — an AI-powered
            patient engagement and appointment scheduling service for dental practices. This Privacy Policy explains
            what information we collect, how we use it, and your rights regarding that information.
          </p>
          <p>
            By using our services you agree to the practices described in this policy.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <p><strong className="text-white">From dental practices (our customers):</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Account information: name, email address, practice name and website.</li>
            <li>Configuration data: widget settings, knowledge base content, notification preferences.</li>
            <li>Practice management system credentials: API endpoint URLs and API keys for integrations such as Open Dental (stored encrypted at rest).</li>
            <li>Google account tokens: OAuth2 refresh tokens issued by Google when a practice connects Google Calendar (see Section 4).</li>
          </ul>
          <p className="mt-3"><strong className="text-white">From patients interacting with the chat widget:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Contact information voluntarily provided: name, phone number, email address.</li>
            <li>Appointment intent: requested date/time, reason for visit, new or existing patient status.</li>
            <li>Conversation transcripts with the AI assistant.</li>
          </ul>
          <p className="mt-3"><strong className="text-white">Automatically collected:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Usage logs, IP addresses, and browser/device type for security and abuse prevention.</li>
            <li>Firebase Analytics events (page views, feature usage) — anonymized and aggregated.</li>
          </ul>
        </Section>

        <Section title="3. How We Use Your Information">
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>To operate and improve the MomoroX Concierge service.</li>
            <li>To route patient appointment requests to the correct dental practice.</li>
            <li>To send appointment confirmation and notification emails to practices.</li>
            <li>To create entries in a practice's connected scheduling or calendar systems.</li>
            <li>To provide customer support and respond to inquiries.</li>
            <li>To detect and prevent fraud, abuse, or security incidents.</li>
          </ul>
          <p>
            We do not sell personal information. We do not use patient conversation data to train AI models
            beyond improving responses within the scope of the service.
          </p>
        </Section>

        <Section title="4. Google Calendar Integration">
          <p>
            Dental practices may optionally connect their Google Calendar account to MomoroX Concierge.
            When a practice connects Google Calendar, we request the following OAuth2 scope:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2 mt-2">
            <li>
              <code className="bg-white/10 px-1 rounded text-gray-300">https://www.googleapis.com/auth/calendar.events</code> —
              allows MomoroX to create, update, and delete calendar events on the practice's primary Google Calendar.
            </li>
          </ul>
          <p className="mt-3"><strong className="text-white">What we do with Google Calendar access:</strong></p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>We <strong className="text-white">create</strong> a calendar event when a patient successfully books an appointment through the chat widget.</li>
            <li>We do <strong className="text-white">not</strong> read, scan, or analyze existing calendar events.</li>
            <li>We do <strong className="text-white">not</strong> share Google Calendar data with any third parties.</li>
            <li>We do <strong className="text-white">not</strong> use Google Calendar data for advertising or to train AI models.</li>
          </ul>
          <p className="mt-3"><strong className="text-white">Token storage:</strong></p>
          <p>
            OAuth2 refresh tokens are stored in Google Firestore, encrypted at rest by Google Cloud.
            Access tokens are obtained on demand and never persisted.
          </p>
          <p className="mt-3"><strong className="text-white">Disconnecting:</strong></p>
          <p>
            A practice can disconnect Google Calendar at any time from the Settings page of their MomoroX dashboard.
            Upon disconnection, the stored refresh token is immediately deleted from our systems. You may also
            revoke access directly from your{' '}
            <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer"
              className="text-electric-teal underline">
              Google Account permissions page
            </a>.
          </p>
          <p className="mt-3">
            MomoroX's use of Google user data complies with the{' '}
            <a href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank" rel="noopener noreferrer" className="text-electric-teal underline">
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements.
          </p>
        </Section>

        <Section title="5. Data Sharing and Third Parties">
          <p>We share information only in these circumstances:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong className="text-white">Service providers:</strong> Google Firebase (hosting, database, authentication), Resend (transactional email), OpenRouter (AI inference). Each is bound by data processing agreements.</li>
            <li><strong className="text-white">The dental practice:</strong> Patient contact information and appointment details are shared with the practice the patient is interacting with.</li>
            <li><strong className="text-white">Legal requirements:</strong> We may disclose information if required by law, court order, or to protect the safety of our users.</li>
          </ul>
          <p>We do not sell, rent, or trade personal information to any third parties for marketing purposes.</p>
        </Section>

        <Section title="6. Data Retention">
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Practice account data is retained while the account is active and deleted within 30 days of account closure upon request.</li>
            <li>Patient conversation data is retained for 12 months and then automatically deleted.</li>
            <li>Google Calendar OAuth tokens are deleted immediately upon disconnection.</li>
            <li>Audit logs are retained for 90 days.</li>
          </ul>
        </Section>

        <Section title="7. Security">
          <p>
            We implement industry-standard security measures including TLS encryption in transit,
            AES-256 encryption at rest via Google Cloud, and access controls limiting who can view
            stored data. No method of transmission or storage is 100% secure; we cannot guarantee
            absolute security.
          </p>
        </Section>

        <Section title="8. HIPAA Notice">
          <p>
            MomoroX Concierge is designed to assist with appointment scheduling and patient intake.
            Dental practices are responsible for their own HIPAA compliance obligations.
            Patient information collected by the widget may constitute Protected Health Information (PHI)
            under HIPAA. Practices that require a Business Associate Agreement (BAA) should contact us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-electric-teal underline">{CONTACT_EMAIL}</a>.
          </p>
        </Section>

        <Section title="9. Your Rights">
          <p>Depending on your jurisdiction, you may have the right to:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Access the personal information we hold about you.</li>
            <li>Request correction of inaccurate data.</li>
            <li>Request deletion of your data.</li>
            <li>Withdraw consent at any time (including disconnecting Google Calendar).</li>
            <li>Lodge a complaint with a supervisory authority.</li>
          </ul>
          <p>To exercise any of these rights, contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-electric-teal underline">{CONTACT_EMAIL}</a>.</p>
        </Section>

        <Section title="10. Children's Privacy">
          <p>
            Our services are not directed to children under 13. We do not knowingly collect personal
            information from children under 13. If you believe we have inadvertently collected such
            information, please contact us and we will delete it promptly.
          </p>
        </Section>

        <Section title="11. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. We will notify practices of material
            changes by email or by posting a notice in the dashboard. The effective date at the top of
            this page will reflect the date of the latest update. Continued use of the service after
            changes constitutes acceptance.
          </p>
        </Section>

        <Section title="12. Contact Us">
          <p>If you have questions or concerns about this Privacy Policy, please contact us:</p>
          <div className="mt-2 bg-white/5 border border-white/10 rounded-lg p-4 text-sm space-y-1">
            <p className="text-white font-semibold">MomoroX, Inc.</p>
            <p>Email: <a href={`mailto:${CONTACT_EMAIL}`} className="text-electric-teal underline">{CONTACT_EMAIL}</a></p>
            <p>Website: <a href="https://momorox.com" className="text-electric-teal underline">https://momorox.com</a></p>
          </div>
        </Section>

        <div className="mt-12 pt-8 border-t border-white/10 flex items-center justify-between">
          <Link to="/" className="text-electric-teal text-sm hover:underline">← Back to MomoroX</Link>
          <Link to="/terms" className="text-gray-500 text-sm hover:text-electric-teal transition-colors">Terms of Service</Link>
          <span className="text-gray-600 text-xs">© {new Date().getFullYear()} MomoroX, Inc.</span>
        </div>
      </div>
    </div>
  );
}
